import { UnitOfMeasureUtil } from './unitOfMeasure.util';

export const UnitSystem = {
  // imperial = 'imperial',
  metric: 'metric',
} as const;

export type UnitSystem = (typeof UnitSystem)[keyof typeof UnitSystem];

export const UnitType = {
  quantity: 'quantity',
  mass: 'mass',
  volume: 'volume',
  length: 'length',

  // Business Central
  unit: 'unit',
  day: 'day',
  set: 'set',
  m2: 'm2',
  m3: 'm3',
  pack: 'pack',
  hour: 'hour',
  vnt: 'vnt',

  // SAP
  pc: 'PC',
  car: 'CAR',
  pal: 'PAL',
} as const;

export type UnitType = (typeof UnitType)[keyof typeof UnitType];

export class MetricUnit {
  static giga = 'giga'; // 10^9
  static mega = 'mega'; // 10^6
  static kilo = 'kilo'; // 10^3
  static hecto = 'hecto'; // 10^2
  static deca = 'deca'; // 10^1
  static base = 'base'; // 10^0
  static deci = 'deci'; // 10^-1
  static centi = 'centi'; // 10^-2
  static milli = 'milli'; // 10^-3
  static micro = 'micro'; // 10^-6
  static nano = 'nano'; // 10^-9

  static value(unit?: Unit): number {
    switch (unit) {
      case MetricUnit.giga:
        return 18;
      case MetricUnit.mega:
        return 15;
      case MetricUnit.kilo:
        return 12;
      case MetricUnit.hecto:
        return 11;
      case MetricUnit.deca:
        return 10;
      case MetricUnit.base:
        return 9;
      case MetricUnit.deci:
        return 8;
      case MetricUnit.centi:
        return 7;
      case MetricUnit.milli:
        return 6;
      case MetricUnit.micro:
        return 3;
      case MetricUnit.nano:
        return 0;
      default:
        throw Error('NOT_FOUND');
    }
  }
}

export type Unit = MetricUnit;

export class UnitOfMeasure {
  system?: UnitSystem;
  unit?: Unit;
  unitType?: UnitType;

  static default(): UnitOfMeasure {
    const uom = new UnitOfMeasure();
    uom.system = UnitSystem.metric;
    uom.unit = MetricUnit.base;
    uom.unitType = UnitType.quantity;
    return uom;
  }

  static toQuantity(uom: UnitOfMeasure = UnitOfMeasure.default(), unitQuantity: string) {
    if (!uom) return unitQuantity;
    if (UnitOfMeasureUtil.isUnitTypeQuantity(uom.unitType ?? UnitType.quantity)) return unitQuantity;
    if (uom.system !== UnitSystem.metric || !uom.unit) return unitQuantity;

    try {
      return UnitOfMeasureUtil.toMetricQuantity(uom, unitQuantity);
    } catch {
      return unitQuantity;
    }
  }

  static toQuantityWithSuffix(uom: UnitOfMeasure = UnitOfMeasure.default(), unitQuantity: string) {
    if (!uom || uom.unitType === UnitType.quantity) return unitQuantity;

    const quantityString = UnitOfMeasure.toQuantity(uom, unitQuantity);
    const suffix = UnitOfMeasureUtil.getSuffix(uom);
    if (!suffix) return quantityString;

    return `${quantityString} ${suffix}`;
  }

  static quantityWithSuffix(uom: UnitOfMeasure = UnitOfMeasure.default(), quantity: string) {
    const suffix = UnitOfMeasureUtil.getSuffix(uom);
    if (!suffix) return quantity;

    return `${quantity} ${suffix}`;
  }
}
